// Asset photos (R2): up to 3 per asset in the private asset-photos bucket, shown through short-lived signed URLs.
// Add = downsize → upload → add_asset_photo (the RPC checks the file, the folder and the limit); a refused add
// removes the uploaded file again. Remove = remove_asset_photo → delete the file (best effort).
import { assetPhotoPath, type AssetPhoto } from '@fleetspoke/core/photos';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downscaleImage } from '../lib/images';
import { supabase } from '../lib/supabase';

const BUCKET = 'asset-photos';

export interface ShownPhoto extends AssetPhoto {
  url: string | null;
}

export interface MountingPhoto {
  tagId: string;
  identifier: string;
  path: string;
  url: string | null;
}

async function signed(paths: string[]): Promise<Map<string, string>> {
  if (!paths.length) return new Map();
  const { data, error } = await supabase().storage.from(BUCKET).createSignedUrls(paths, 3600);
  if (error) throw new Error(error.message);
  return new Map(
    (data ?? []).flatMap((d) => (d.path && d.signedUrl ? [[d.path, d.signedUrl] as const] : [])),
  );
}

export const photoKeys = {
  asset: (id: string) => ['asset-photos', id] as const,
  firsts: ['asset-photos', 'first'] as const,
};

export function useAssetPhotos(assetId: string) {
  return useQuery({
    queryKey: photoKeys.asset(assetId),
    queryFn: async () => {
      const [asset, tags] = await Promise.all([
        supabase().from('assets').select('photos').eq('id', assetId).single(),
        supabase()
          .from('tags')
          .select('id, identifier, mounting_photo_url')
          .eq('asset_id', assetId)
          .not('mounting_photo_url', 'is', null),
      ]);
      if (asset.error) throw new Error(asset.error.message);
      if (tags.error) throw new Error(tags.error.message);
      const photos = (asset.data.photos ?? []) as AssetPhoto[];
      const mounting = (tags.data ?? []) as {
        id: string;
        identifier: string;
        mounting_photo_url: string;
      }[];
      const urls = await signed([
        ...photos.map((p) => p.path),
        ...mounting.map((m) => m.mounting_photo_url),
      ]);
      return {
        photos: photos.map((p): ShownPhoto => ({ ...p, url: urls.get(p.path) ?? null })),
        mounting: mounting.map((m): MountingPhoto => ({
          tagId: m.id,
          identifier: m.identifier,
          path: m.mounting_photo_url,
          url: urls.get(m.mounting_photo_url) ?? null,
        })),
      };
    },
    staleTime: 30 * 60_000, // signed URLs last an hour
  });
}

/**
 * First photo of each listed asset (Assets list thumbnails): one `assets.photos` read + one batch of signed URLs per
 * chunk of ids (a long `in (...)` would outgrow the request URL). The full-size file is shown small — the rows load it
 * lazily; a stored thumbnail is the follow-up if long lists get heavy.
 */
export function useFirstPhotoUrls(assetIds: string[]) {
  const ids = [...assetIds].sort();
  return useQuery({
    queryKey: [...photoKeys.firsts, ids],
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const firsts = new Map<string, string>();
      for (let i = 0; i < ids.length; i += 100) {
        const { data, error } = await supabase()
          .from('assets')
          .select('id, photos')
          .in('id', ids.slice(i, i + 100));
        if (error) throw new Error(error.message);
        for (const row of (data ?? []) as { id: string; photos: AssetPhoto[] | null }[]) {
          const first = row.photos?.[0];
          if (first) firsts.set(row.id, first.path);
        }
      }
      const urls = await signed([...firsts.values()]);
      return new Map(
        [...firsts].flatMap(([id, path]) => {
          const url = urls.get(path);
          return url ? [[id, url] as const] : [];
        }),
      );
    },
    staleTime: 30 * 60_000, // signed URLs last an hour
  });
}

function rpcError(error: { message: string; code?: string }) {
  return Object.assign(new Error(error.message), { code: error.code });
}

export function useAddAssetPhoto(companyId: string, assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const jpeg = await downscaleImage(file);
      const path = assetPhotoPath(companyId, assetId, crypto.randomUUID());
      const up = await supabase()
        .storage.from(BUCKET)
        .upload(path, jpeg, { contentType: 'image/jpeg', upsert: false });
      if (up.error) throw new Error(up.error.message);
      const { error } = await supabase().rpc('add_asset_photo', {
        p_idempotency_key: crypto.randomUUID(),
        p_asset_id: assetId,
        p_path: path,
      });
      if (error) {
        await supabase().storage.from(BUCKET).remove([path]); // refused (e.g. already 3): no orphan file
        throw rpcError(error);
      }
      return { path, bytes: jpeg.size };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: photoKeys.asset(assetId) });
      void queryClient.invalidateQueries({ queryKey: photoKeys.firsts });
    },
  });
}

export function useRemoveAssetPhoto(assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (path: string) => {
      const { error } = await supabase().rpc('remove_asset_photo', {
        p_idempotency_key: crypto.randomUUID(),
        p_asset_id: assetId,
        p_path: path,
      });
      if (error) throw rpcError(error);
      await supabase().storage.from(BUCKET).remove([path]);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: photoKeys.asset(assetId) });
      void queryClient.invalidateQueries({ queryKey: photoKeys.firsts });
    },
  });
}
